/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */

//handle CSR user status including available store list
//standard libraries
import React, { createContext, useContext, useReducer } from "react";

//custom libraries
import * as ACTIONS from "../constants/actions";
import { CSR } from "../constants/common";
import { localStorageHelper } from "../helpers/storageHelper";

export interface CSRStateInterface {
  WCToken?: string;
  WCTrustedToken?: string;
  lastUpdated?: number;
  storeList?: [];
  userLoggedIn?: boolean;
  [extraProps: string]: any;
}

/**
 * StoreList example:
 * [
 *  {
        "id": 11,
        "ownerId": "-11000",
        "type": "MHS",
        "state": "open",
        "identifier": "Emerald",
        "catalogOverrideEnabled": "1",
        "externalContentEnablementMode": "false",
        "externalContentPickerUrl": "/wps/mycontenthandler/dxpicker/dam-files",
        "externalContentDAMFilePickerUrl": "/wps/mycontenthandler/dxpicker/dam-files",
        "externalContentDAMImagePickerUrl": "/wps/mycontenthandler/dxpicker/dam-images",
        "externalContentAPIContextRoot": "/dx/api/core/v1",
        "externalContentAPIContentPath": "/dxrest/webcontent/contents",
        "hclImagePath": "/hclstore",
        "imagePathFilter": null,
        "externalContentHost": "https://www-dx.commercetest1.tk",
        "headlessStore": "TRUE",
        "headlessStoreContextRoot": "/Emerald",
        "unregisterMarketingEvent": "0",
        "descriptions" : [
          {
            "onlineStoreId": 11,
            "id": -1,
            "name": "Emerald",
            "description": "Commerce Model Store entity"
        }
        ]
    }
 * ]
 */
const csrInitstate: CSRStateInterface = {};

const CSRReducer = (state: CSRStateInterface, action: any) => {
  switch (action.type) {
    case ACTIONS.LOGIN_SUCCESS_ACTION: {
      const stateClone = { ...state };
      const session = Object.assign(
        {},
        {
          ...stateClone,
          ...action.payload,
          userLoggedIn: true,
        }
      );
      localStorageHelper.set(CSR, session);
      return session;
    }
    case ACTIONS.SESSION_ERROR_ACTION: {
      const csr = Object.assign(
        {},
        {
          ...csrInitstate,
          ...action.payload,
        }
      );
      localStorageHelper.set(CSR, csr);
      return csr;
    }

    case ACTIONS.RESET_ERROR_ACTION: {
      const stateClone = { ...state };
      delete stateClone.sessionError;
      const csr = Object.assign(
        {},
        {
          ...stateClone,
        }
      );
      localStorageHelper.set(CSR, csr);
      return csr;
    }

    case ACTIONS.LOGOUT_SUCCESS_ACTION: {
      const csr = {
        ...csrInitstate,
      };
      localStorageHelper.set(CSR, csr);
      return csr;
    }

    case ACTIONS.EXPIRED_CSR_ACTION: {
      console.log("Expired CSR", state);
      console.log("Expired CSR2", action.payload);
      return {
        ...state,
        ...action.payload,
      };
    }

    default:
      throw new Error(`Action is not supported: ${action.type}`);
  }
};

const csrInitializer = (initstate: CSRStateInterface) => {
  //initialize csr state from storage.
  localStorageHelper.invalidateAllIfExpired();
  let csr = localStorageHelper.get(CSR);
  if (csr) {
    delete csr.sessionError;
    return csr;
  } else {
    return initstate;
  }
};

export const CSRContext: React.Context<any> = createContext({});
export const CSRProvider = ({ children }: any) => {
  const [csr, dispatch] = useReducer(CSRReducer, csrInitstate, csrInitializer);
  const value = React.useMemo(() => ({ csr, dispatch }), [csr]);

  return <CSRContext.Provider value={value}>{children}</CSRContext.Provider>;
};

export const useCSRValue = () => useContext(CSRContext);
